import type { ListUserSettingInputs, UserSettingInput } from "@scayla/api-client-fetch";
import {
  moduleSettingGet,
  moduleSettingsGet,
  moduleSettingsUpdate,
  settingGet,
  SettingName,
} from "@scayla/api-client-fetch";
import { defineStore } from "pinia";
import { computed } from "vue";

import { useWorkspace } from "@/store/workspace.ts";

const settingIdCache = new Map<string, string>();

export const TEMPLATE_SECTION_EXPAND_TYPE = SettingName.TEMPLATE_SECTION_EXPAND_TYPE;
export const LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE = SettingName.LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE;
export const OLDER_TABLE_ENTRIES_EXPAND_TYPE = SettingName.OLDER_TABLE_ENTRIES_EXPAND_TYPE;

export const useModuleSettingsStore = defineStore("moduleSettings", () => {
  const workspace = useWorkspace();
  const tenantId = computed(() => workspace.tenantId);

  async function getSettingId(settingName: string): Promise<string | undefined> {
    if (settingIdCache.has(settingName)) {
      return settingIdCache.get(settingName) ?? undefined;
    }

    try {
      const response = await settingGet({
        path: { settingName },
      });
      const settingId = response.data?.id;
      if (settingId) {
        settingIdCache.set(settingName, settingId);
      }
      return settingId;
    } catch (error) {
      console.warn(`Failed to get setting ID for ${settingName}:`, error);
    }
    return undefined;
  }

  async function createSettingInput(
    moduleId: string,
    settingName: string,
    value: unknown,
    keySuffix?: string,
  ): Promise<UserSettingInput> {
    const settingId = await getSettingId(settingName);
    if (!settingId) {
      throw new Error(`Setting ${settingName} not found`);
    }

    return {
      settingId,
      moduleId,
      userId: workspace.user?.id,
      tenantId: tenantId.value!,
      keySuffix,
      value,
    };
  }

  async function fetchModuleSetting(moduleId: string, settingName: string, keySuffix?: string) {
    if (!tenantId.value || !moduleId) return undefined;

    try {
      const response = await moduleSettingGet({
        path: { moduleId, settingName },
        query: {
          tenantId: tenantId.value,
          ...(keySuffix !== undefined ? { keySuffix } : {}),
        },
      });

      if (response.data?.settingId && response.data.name) {
        settingIdCache.set(response.data.name, response.data.settingId);
      }

      return response.data ?? undefined;
    } catch (error) {
      console.warn(
        `Failed to load module setting for ${moduleId}/${settingName}${keySuffix ? `/${keySuffix}` : ""}:`,
        error,
      );
      return undefined;
    }
  }

  async function fetchModuleSettings(moduleId: string, allKeySuffixes?: boolean) {
    if (!tenantId.value || !moduleId) return undefined;

    try {
      const response = await moduleSettingsGet({
        path: { moduleId },
        query: {
          tenantId: tenantId.value,
          ...(allKeySuffixes !== undefined ? { allKeySuffixes } : {}),
        },
      });

      if (response.data?.items) {
        response.data.items.forEach((setting) => {
          if (setting.settingId && setting.name) {
            settingIdCache.set(setting.name, setting.settingId);
          }
        });
      }

      return response.data ?? undefined;
    } catch (error) {
      console.warn(`Failed to load module settings for ${moduleId}:`, error);
      return undefined;
    }
  }

  async function updateModuleSettings(moduleId: string, settings: UserSettingInput[]) {
    try {
      const response = await moduleSettingsUpdate({
        path: { moduleId },
        query: { tenantId: tenantId.value! },
        body: {
          items: settings,
          maxLength: settings.length,
        } as ListUserSettingInputs,
      });

      return response.data;
    } catch (error) {
      console.error(`Error updating module settings for ${moduleId}:`, error);
      throw error;
    }
  }

  async function saveModuleSetting(
    moduleId: string,
    settingName: string,
    value: unknown,
    keySuffix?: string,
  ): Promise<void> {
    const settingInput = await createSettingInput(moduleId, settingName, value, keySuffix);
    await updateModuleSettings(moduleId, [settingInput]);
  }

  async function saveModuleSettings(
    moduleId: string,
    settings: {
      settingName: string;
      value: unknown;
      keySuffix?: string;
    }[],
  ) {
    const settingsInput = await Promise.all(
      settings.map((s) => createSettingInput(moduleId, s.settingName, s.value, s.keySuffix)),
    );
    await updateModuleSettings(moduleId, settingsInput);
  }

  return {
    fetchModuleSetting,
    fetchModuleSettings,
    saveModuleSetting,
    saveModuleSettings,
  };
});
